// Slim navigation catalog. Powers the home grid, the header "Tools" menu,
// and the page-header "use another tool" dropdown — nothing else.
//
// Per-page copy (h1 tagline, hero description, file-input config) lives in
// each route's own view file. This catalog is intentionally small: adding
// fields here couples every route to a shared schema and we don't want that.
//
// `accept` is the one exception: the home drop-zone needs to know which
// tools can open which file types, and the cards display this as a chip.
export type Category = "combine" | "edit" | "convert";

export interface Action {
  id: string;
  title: string;
  shortDescription: string;
  link: { url: string; title: string };
  category: Category;
  accept: string[]; // file extensions, lowercase, leading dot
  upcoming?: boolean;
}

export const CATEGORY_LABELS: Record<Category, string> = {
  combine: "Combine",
  edit: "Edit",
  convert: "Convert",
};

const raw: (Action & { index: number })[] = [
  {
    id: "merge-pdf",
    title: "Merge PDFs",
    shortDescription: "Combine PDFs and images into one file.",
    link: { url: "/merge", title: "Merge files" },
    category: "combine",
    accept: [".pdf", ".jpg", ".jpeg", ".png", ".webp"],
    index: 0,
  },
  {
    id: "image-to-pdf",
    title: "Image to PDF",
    shortDescription: "JPG, PNG, or WebP into a single PDF.",
    link: { url: "/image-to-pdf", title: "Convert images" },
    category: "convert",
    accept: [".jpg", ".jpeg", ".png", ".webp"],
    index: 1,
  },
  {
    id: "split-pdf",
    title: "Split PDF",
    shortDescription: "Custom or fixed-size page ranges.",
    link: { url: "/split", title: "Split file" },
    category: "edit",
    accept: [".pdf"],
    index: 2,
  },
  {
    id: "delete-pages",
    title: "Delete pages",
    shortDescription: "Remove the pages you don't need.",
    link: { url: "/delete-pages", title: "Delete pages" },
    category: "edit",
    accept: [".pdf"],
    index: 3,
  },
  {
    id: "organize-pdf",
    title: "Organize PDF",
    shortDescription: "Drag pages into the right order.",
    link: { url: "/organize-pdf", title: "Reorder pages" },
    category: "edit",
    accept: [".pdf"],
    index: 4,
  },
  {
    id: "add-watermark",
    title: "Add watermark",
    shortDescription: "Text or image, stamped on every page.",
    link: { url: "/add-watermark", title: "Add watermark" },
    category: "edit",
    accept: [".pdf"],
    index: 5,
  },
  {
    id: "page-numbering",
    title: "Page numbers",
    shortDescription: "Number every page automatically.",
    link: { url: "/page-numbering", title: "Add numbers" },
    category: "edit",
    accept: [".pdf"],
    index: 6,
  },
  {
    id: "email-to-pdf",
    title: "Email to PDF",
    shortDescription: "Turn .eml emails into print-ready PDFs.",
    link: { url: "/email-to-pdf", title: "Convert .eml" },
    category: "convert",
    accept: [".eml"],
    index: 7,
  },
  {
    id: "pdf-to-ppt",
    title: "PDF to PPTX",
    shortDescription: "Editable slides from any PDF.",
    link: { url: "/pdf-to-ppt", title: "Convert to slides" },
    category: "convert",
    accept: [".pdf"],
    upcoming: true,
    index: 8,
  },
  {
    id: "html-to-pdf",
    title: "HTML to PDF",
    shortDescription: "Render web pages as PDFs.",
    link: { url: "/html-to-pdf", title: "Render page" },
    category: "convert",
    accept: [".html"],
    upcoming: true,
    index: 9,
  },
  {
    id: "chat-with-pdf",
    title: "Ask PDF",
    shortDescription: "Chat with your PDF using AI.",
    link: { url: "/chat-with-pdf", title: "Start chat" },
    category: "edit",
    accept: [".pdf"],
    upcoming: true,
    index: 10,
  },
  {
    id: "summarize-pdf",
    title: "Summarize PDF",
    shortDescription: "AI-generated summaries.",
    link: { url: "/summarize-pdf", title: "Summarize" },
    category: "edit",
    accept: [".pdf"],
    upcoming: true,
    index: 11,
  },
];

export const actions: Action[] = raw
  .sort((a, b) => {
    if (a.upcoming && !b.upcoming) return 1;
    if (!a.upcoming && b.upcoming) return -1;
    return a.index - b.index;
  })
  .map(({ index: _index, ...rest }) => rest);

// Group active (non-upcoming) actions by category, preserving catalog order.
export function actionsByCategory(): { category: Category; actions: Action[] }[] {
  const order: Category[] = ["combine", "edit", "convert"];
  const live = actions.filter((a) => !a.upcoming);
  return order
    .map((category) => ({ category, actions: live.filter((a) => a.category === category) }))
    .filter((group) => group.actions.length > 0);
}

export const upcomingActions = actions.filter((a) => a.upcoming);

// Find the tools that accept a given file extension. Used by the home
// drop-zone to surface a picker after a file is dropped.
export function findToolsForExtension(extension: string): Action[] {
  const ext = extension.toLowerCase();
  return actions.filter((a) => !a.upcoming && a.accept.includes(ext));
}

export function actionById(id: string): Action | undefined {
  return actions.find((a) => a.id === id);
}

// Find tools that accept *every* extension in the set — i.e. tools that can
// open a mixed pile of files together (merge-pdf with PDFs + images). An
// empty set returns no tools so callers don't have to special-case it.
export function findToolsForExtensions(extensions: string[]): Action[] {
  if (extensions.length === 0) return [];
  const exts = Array.from(new Set(extensions.map((e) => e.toLowerCase())));
  return actions.filter((a) => !a.upcoming && exts.every((e) => a.accept.includes(e)));
}
